import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ApiError } from '@/core/api';
import { useAcademicYears } from '@/features/schools';
import { useClasses, useCreateClass, useUpdateClass } from '../queries';
import type { SchoolClassDto } from '../types';

type Translate = (key: string) => string;

const makeSchema = (t: Translate) =>
  z.object({
    name: z.string().min(1, t('classes:validation.nameRequired')),
    description: z.string(),
  });

type FormValues = z.infer<ReturnType<typeof makeSchema>>;
const FIELD_KEYS = ['name', 'description'] as const;
/** The backend's `max_page_size`; asking for more is silently clamped. */
const SUGGESTION_PAGE_SIZE = 50;
const NAME_SUGGESTIONS_ID = 'class-name-suggestions';

interface ClassFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present ⇒ edit mode (rename + description). Absent ⇒ create. */
  schoolClass?: SchoolClassDto | null;
}

/**
 * Create a class, or rename one and rewrite its description.
 *
 * Teachers are a separate question and stay in `ManageTeachersDialog`: both
 * write through the same PATCH, but sending `teachers` from here would let an
 * unrelated rename overwrite the assignment list with whatever this form last
 * saw. Who may save (admin, or a teacher of THIS class) is the server's call —
 * its refusal is rendered, not predicted.
 */
export function ClassFormDialog({ open, onOpenChange, schoolClass }: ClassFormDialogProps) {
  const { t } = useTranslation(['classes', 'common']);
  const navigate = useNavigate();
  const editing = Boolean(schoolClass);
  const createClass = useCreateClass();
  const updateClass = useUpdateClass(schoolClass?.public_id ?? '');
  // ADR-031: a new class binds to a year at create (default: the current
  // one); afterwards the year is frozen — `promote/` is the year-end path.
  const years = useAcademicYears({ enabled: open && !editing });
  const currentYearId = years.data?.results.find(y => y.is_current)?.public_id ?? '';
  const [yearId, setYearId] = useState('');
  const selectedYear = yearId || currentYearId;

  // Naming is a convention, not a constraint (ADR-038): the field stays free
  // text, and the existing names are offered as a datalist so an admin picks
  // «6-А Інформатика (гр1)» and edits the subgroup rather than retyping the
  // subject — convergence without refusing the tenants whose classes look
  // nothing like a Ukrainian cohort ("IELTS Evening Group", PRD §4.4).
  const siblings = useClasses({ page_size: SUGGESTION_PAGE_SIZE }, { enabled: open });
  const suggestions = useMemo(() => {
    const names = (siblings.data?.results ?? [])
      .map(c => c.name)
      .filter(name => name !== schoolClass?.name);
    return [...new Set(names)].sort((a, b) => a.localeCompare(b));
  }, [siblings.data, schoolClass?.name]);

  const schema = useMemo(() => makeSchema(t), [t]);
  const defaults = {
    name: schoolClass?.name ?? '',
    description: schoolClass?.description ?? '',
  };
  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: defaults });

  function close(next: boolean) {
    onOpenChange(next);
    if (!next) form.reset(defaults);
  }

  async function onSubmit(values: FormValues) {
    try {
      if (schoolClass) {
        await updateClass.mutateAsync(values);
        toast.success(t('classes:edit.success'));
        onOpenChange(false);
        return;
      }
      const created = await createClass.mutateAsync({
        ...values,
        ...(selectedYear ? { academic_year: selectedYear } : {}),
      });
      toast.success(t('classes:create.success'));
      form.reset(defaults);
      onOpenChange(false);
      navigate(`/classes/${created.public_id}`);
    } catch (e) {
      const err = e as ApiError;
      const fe = err?.fieldErrors;
      if (fe) {
        for (const [field, messages] of Object.entries(fe)) {
          const key = FIELD_KEYS.find(k => k === field);
          if (key) form.setError(key, { message: messages.join(' ') });
        }
      }
      const general = fe?.non_field_errors;
      if (general || !fe) {
        form.setError('root', {
          message: general
            ? general.join(' ')
            : (err?.message ?? t(editing ? 'classes:edit.failed' : 'classes:create.failed')),
        });
      }
    }
  }

  const submitting = form.formState.isSubmitting;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(editing ? 'classes:edit.title' : 'classes:create.title')}</DialogTitle>
          <DialogDescription>
            {t(editing ? 'classes:edit.description' : 'classes:create.description')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('classes:create.name')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('classes:create.namePlaceholder')}
                      list={suggestions.length ? NAME_SUGGESTIONS_ID : undefined}
                      {...field}
                    />
                  </FormControl>
                  {suggestions.length ? (
                    <datalist id={NAME_SUGGESTIONS_ID}>
                      {suggestions.map(name => (
                        <option key={name} value={name} />
                      ))}
                    </datalist>
                  ) : null}
                  <FormDescription>{t('classes:create.nameHint')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('classes:create.descriptionLabel')}</FormLabel>
                  <FormControl>
                    <Textarea placeholder={t('classes:create.descriptionPlaceholder')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {!editing ? (
              <div className="space-y-1.5">
                <Label htmlFor="class-year">{t('classes:create.yearLabel')}</Label>
                <Select value={selectedYear} onValueChange={setYearId}>
                  <SelectTrigger id="class-year" className="w-full">
                    <SelectValue placeholder={t('classes:create.yearPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {(years.data?.results ?? []).map(year => (
                      <SelectItem key={year.public_id} value={year.public_id}>
                        {year.name}
                        {year.is_current ? ` — ${t('classes:create.yearCurrent')}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            {form.formState.errors.root ? (
              <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => close(false)}>
                {t('common:cancel')}
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting
                  ? t(editing ? 'classes:edit.submitting' : 'classes:create.submitting')
                  : t(editing ? 'classes:edit.submit' : 'classes:create.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
